import { useQuery } from "@apollo/client";
import { useState } from "react";
import { Link } from "react-router-dom";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { Container, makeStyles } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { Alert } from "@material-ui/lab";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import { useSubaccount } from "src/components/SubaccountProvider";
import Table, { TableControls } from "src/components/Table";
import {
  createBlockedPhoneNumber,
  createBlockedPhoneNumberVariables
} from "src/graphql/generated/createBlockedPhoneNumber";
import {
  deleteBlockedPhoneNumber,
  deleteBlockedPhoneNumberVariables
} from "src/graphql/generated/deleteBlockedPhoneNumber";
import {
  listBlockedPhoneNumbers,
  listBlockedPhoneNumbersVariables,
  listBlockedPhoneNumbers_listBlockedPhoneNumbers_blockedPhoneNumbers
} from "src/graphql/generated/listBlockedPhoneNumbers";
import useMutation from "src/util/useMutation";
import yup from "src/util/yup";

import AddBlockedPhoneNumberDialog, {
  AddBlockedPhoneNumberValues
} from "./AddBlockedPhoneNumberDialog";
import {
  CREATE_BLOCKED_PHONE_NUMBER,
  DELETE_BLOCKED_PHONE_NUMBER,
  LIST_BLOCKED_PHONE_NUMBERS
} from "./queries";

type BlockedPhoneNumber =
  listBlockedPhoneNumbers_listBlockedPhoneNumbers_blockedPhoneNumbers;

const useStyles = makeStyles(theme => ({
  alert: {
    marginTop: theme.spacing(2)
  }
}));

const BlockedPhoneNumbers: React.FC = () => {
  const classes = useStyles();
  const { subaccount, subaccountLink } = useSubaccount()!;
  const verificationRequired = subaccount.settings.smsVerificationRequired;

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const { loading, error, data } = useQuery<
    listBlockedPhoneNumbers,
    listBlockedPhoneNumbersVariables
  >(LIST_BLOCKED_PHONE_NUMBERS, {
    variables: {
      filter: { phoneNumberContains: search },
      offset: page * pageSize,
      limit: pageSize,
      subaccountId: subaccount.id
    }
  });

  const [addDialogOpen, toggleAddDialog] = useState(false);
  const [addBlockedPhoneNumber] = useMutation<
    createBlockedPhoneNumber,
    createBlockedPhoneNumberVariables
  >(CREATE_BLOCKED_PHONE_NUMBER, {
    onErrorSnackbarMessage: "Error blocking phone number",
    refetchQueries: ["listBlockedPhoneNumbers"],
    awaitRefetchQueries: true
  });

  async function handleAddBlockedPhoneNumber(
    values: AddBlockedPhoneNumberValues
  ) {
    const { errors } = await addBlockedPhoneNumber({
      variables: { ...values, subaccountId: subaccount.id }
    });
    if (errors) {
      throw errors;
    }
    toggleAddDialog(false);
  }

  const [removeBlockedPhoneNumber] = useMutation<
    deleteBlockedPhoneNumber,
    deleteBlockedPhoneNumberVariables
  >(DELETE_BLOCKED_PHONE_NUMBER, {
    onErrorSnackbarMessage: "Error unblocking phone number",
    refetchQueries: ["listBlockedPhoneNumbers"]
  });

  async function handleUnblock(blocked: BlockedPhoneNumber) {
    await removeBlockedPhoneNumber({
      variables: { id: blocked.id, subaccountId: subaccount.id }
    });
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <Container>
      <PageTitle title="Blocked Callers" />
      <div>
        Blocked phone numbers cannot verify or join calling sessions. Anyone
        already calling from a blocked number is signed out immediately.
      </div>
      {!verificationRequired && (
        <Alert severity="warning" className={classes.alert}>
          Blocking has no effect until{" "}
          <Link to={subaccountLink("/settings")}>
            require phone verification for callers
          </Link>{" "}
          is turned on.
        </Alert>
      )}
      <TableControls
        addButtons={[
          {
            label: "Block a Caller",
            onClick: () => toggleAddDialog(true)
          }
        ]}
        filters={[]}
        search={{
          value: search,
          onChange: term => {
            setSearch(term);
            setPage(0);
          },
          placeholder: "Search for a phone number...",
          schema: yup
            .string()
            .test(
              "phoneNumberSubstring",
              "Please include at least 3 digits",
              value =>
                !value ||
                value.length === 0 ||
                value.replace(/\D/g, "").length >= 3
            )
        }}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <Table
          columns={[
            {
              key: "phoneNumber",
              title: "Phone Number",
              renderValue: blocked => blocked.phoneNumber
            },
            {
              key: "note",
              title: "Note",
              renderValue: blocked => blocked.note ?? ""
            },
            {
              key: "createdBy",
              title: "Blocked By",
              renderValue: blocked => blocked.createdByAdmin?.fullName ?? ""
            },
            {
              key: "createdAt",
              title: "Blocked At",
              renderValue: blocked =>
                new Date(blocked.createdAt).toLocaleString()
            }
          ]}
          rows={data?.listBlockedPhoneNumbers.blockedPhoneNumbers ?? []}
          rowActions={[
            {
              tooltipLabel: "Allow this phone number to verify again",
              onClick: handleUnblock,
              confirm: blocked => ({
                title: "Unblock Caller",
                description: (
                  <p>
                    Are you sure you would like to let{" "}
                    <strong>{blocked.phoneNumber}</strong> verify and join
                    calling sessions again?
                  </p>
                )
              }),
              icon: <DeleteIcon />
            }
          ]}
          totalCount={data?.listBlockedPhoneNumbers.totalCount ?? 0}
          rowsPerPage={pageSize}
          onChangeRowsPerPage={newPageSize => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          page={page}
          onChangePage={setPage}
          emptyState="No blocked callers"
        />
      )}
      <AddBlockedPhoneNumberDialog
        open={addDialogOpen}
        onClose={() => toggleAddDialog(false)}
        onSubmit={handleAddBlockedPhoneNumber}
      />
    </Container>
  );
};

export default BlockedPhoneNumbers;
